"use client";

import { createTheme } from "@mui/material/styles";
import type { Components, Theme as MuiTheme } from "@mui/material/styles";

// Import sub-modules
import { colors } from "./colors";
import { sizes, borderRadius } from "./sizes";
import { breakpoints, mediaQueries } from "./mediaQueries";
import {
  fontFamily,
  fontWeights,
  typographyVariants,
  variantToCSS,
  variantDesktopCSS,
} from "./typography";
import { GlobalStyle } from "./GlobalStyle";

// Import types (side-effect: registers module augmentations)
import "./types";

// ---------------------------------------------------------------------------
// Convenience alias: the semantic color set for the active mode.
// ---------------------------------------------------------------------------
const c = colors.light;

// =============================================================================
// PALETTE
// Maps semantic color tokens to MUI's palette structure.
// =============================================================================
const palette = {
  primary: {
    main: c.text.primary,
    dark: c.button.primaryPress,
    light: c.button.primaryHover,
    contrastText: c.text.secondary,
  },
  secondary: {
    main: c.button.primaryDefault,
    dark: c.button.primaryPress,
    light: c.button.primaryHover,
    contrastText: c.text.primary,
  },
  error: {
    main: c.text.error,
  },
  warning: {
    main: c.text.warning,
  },
  success: {
    main: c.text.success,
  },
  background: {
    default: c.bg.primary,
    paper: c.bg.secondary,
  },
  text: {
    primary: c.text.primary,
    secondary: c.text.disabled,
    disabled: c.text.disabled,
  },
  divider: c.border.default,
};

// =============================================================================
// TYPOGRAPHY
// Builds MUI typography config from typography.ts variants.
// Mobile values are the base; desktop overrides are injected via media queries.
// =============================================================================

/**
 * Helper: builds a single MUI typography variant entry with responsive sizing.
 * Returns the mobile styles as base, plus a media query key for desktop.
 */
function responsiveVariant(variantName: string, textColor?: string) {
  const variant = typographyVariants[variantName as keyof typeof typographyVariants];
  if (!variant) return {};

  return {
    ...variantToCSS(variant),
    ...(textColor && { color: textColor }),
    [mediaQueries.desktop]: {
      ...variantDesktopCSS(variant),
    },
  };
}

const typographyConfig = {
  fontFamily: `${fontFamily.primary}, sans-serif`,

  // Standard MUI variants mapped from Figma typography tokens
  h1: responsiveVariant("h1", c.text.primary),
  h2: responsiveVariant("h2", c.text.primary),
  h3: responsiveVariant("h3", c.text.primary),
  subtitle1: responsiveVariant("subtitle1", c.text.primary),
  body1: responsiveVariant("body1", c.text.primary),
  body2: responsiveVariant("body2", c.text.primary),
  button: {
    ...responsiveVariant("body1"),
    textTransform: "none" as const,
    fontWeight: fontWeights.bold,
  },

  // Custom variants (must match types.ts declarations)
  body3: responsiveVariant("body3", c.text.primary),
  body4: responsiveVariant("body4", c.text.primary),
  number: responsiveVariant("number", c.text.primary),
};

// =============================================================================
// COMPONENT OVERRIDES
// Production-quality overrides for MUI components.
// =============================================================================

const components: Components<MuiTheme> = {
  // -------------------------------------------------------------------------
  // CssBaseline - injects GlobalStyle
  // -------------------------------------------------------------------------
  MuiCssBaseline: {
    styleOverrides: GlobalStyle,
  },

  // -------------------------------------------------------------------------
  // Button - custom variants with full state handling
  // -------------------------------------------------------------------------
  MuiButton: {
    defaultProps: {
      disableRipple: true,
      disableElevation: true,
    },
    styleOverrides: {
      root: {
        fontFamily: `${fontFamily.primary}, sans-serif`,
        fontWeight: fontWeights.bold,
        borderRadius: `${borderRadius.full}px`,
        textTransform: "none",
        // EGDE-355: never transition `background` — the primary CTA default is a
        // gradient and its hover is a solid color; a gradient↔solid swap is not
        // interpolable, so `transition: all` makes it flicker on hover. Animate
        // only the interpolable properties; the background swap is instant.
        transition:
          "color 0.2s ease-in-out, box-shadow 0.2s ease-in-out, border-color 0.2s ease-in-out, opacity 0.2s ease-in-out",
        minWidth: "110px",

        // Accessibility: visible focus ring for keyboard navigation
        "&:focus-visible": {
          outline: "2px solid black",
          outlineOffset: "2px",
          border: "1px dashed white",
        },
      },
      sizeLarge: {
        padding: "12px 16px",
        fontSize: "20px",
        lineHeight: "28px",
        height: 52,
        [mediaQueries.mobile]: {
          height: 46,
        },
      },
      sizeMedium: {
        padding: "12px 16px",
        fontSize: "18px",
        lineHeight: "26px",
        height: 48,
        [mediaQueries.mobile]: {
          height: 46,
        },
      },
      sizeSmall: {
        padding: "8px 16px",
        fontSize: "14px",
        lineHeight: "normal",
        height: 36,
        borderRadius: `${borderRadius[8]}px`,
      },
    },
    variants: [
      // Primary: gradient CTA style
      {
        props: { variant: "primary" },
        style: {
          background: colors.gradients.gradientCta,
          color: c.text.primary,

          "&:hover, &:focus-visible": {
            backgroundColor: c.button.primaryHover,
            background: c.button.primaryHover,
            boxShadow: colors.shadows.button,
          },
          "&:active": {
            backgroundColor: c.button.primaryPress,
            background: c.button.primaryPress,
            boxShadow: "none",
          },
          "&.Mui-disabled": {
            backgroundColor: c.button.primaryDisabled,
            background: c.button.primaryDisabled,
            color: c.text.disabled,
          },
        },
      },
      // Secondary: bordered style
      {
        props: { variant: "secondary" },
        style: {
          border: `1px solid ${c.button.primaryDefault}`,
          color: c.text.primary,
          backgroundColor: c.button.secondaryDefault,

          "&:hover, &:focus-visible": {
            backgroundColor: c.button.secondaryHover,
            color: c.text.primary,
            boxShadow: colors.shadows.button,
          },
          "&:active": {
            backgroundColor: c.button.secondaryPress,
            color: c.text.primary,
            boxShadow: "none",
          },
          "&.Mui-disabled": {
            backgroundColor: c.button.primaryDisabled,
            color: c.text.disabled,
            border: "none",
          },
        },
      },
      // White: solid white background
      {
        props: { variant: "white" },
        style: {
          backgroundColor: c.button.secondaryDefault,
          color: c.text.primary,

          "&:hover, &:focus-visible": {
            backgroundColor: c.button.secondaryDefault,
            boxShadow: colors.shadows.button,
          },
          "&:active": {
            backgroundColor: c.button.secondaryDefault,
            border: `1px solid ${c.button.primaryDefault}`,
            boxShadow: colors.shadows.button,
          },
          "&.Mui-disabled": {
            backgroundColor: c.button.secondaryDisabled,
            color: c.text.disabled,
          },
        },
      },
      // Link: text-only style
      {
        props: { variant: "link" },
        style: {
          backgroundColor: "transparent",
          color: c.text.primary,
          minWidth: "auto",
          padding: 0,

          "&:hover, &:focus-visible": {
            backgroundColor: "transparent",
            textDecoration: "underline",
          },
          "&:active": {
            backgroundColor: "transparent",
          },
          "&.Mui-disabled": {
            backgroundColor: "transparent",
            color: c.text.disabled,
          },
        },
      },
    ],
  },

  // -------------------------------------------------------------------------
  // Paper - card/dropdown surfaces
  // -------------------------------------------------------------------------
  MuiPaper: {
    styleOverrides: {
      root: {
        borderRadius: `${borderRadius[12]}px`,
      },
    },
  },

  // -------------------------------------------------------------------------
  // InputBase - shared input styles
  // -------------------------------------------------------------------------
  MuiInputBase: {
    styleOverrides: {
      root: {
        fontFamily: `${fontFamily.primary}, sans-serif`,

        "&.MuiInput-root::before": {
          borderBottom: `1px solid ${c.inputs.defaultDefault}`,
          opacity: 0.3,
        },
        "&.MuiInput-root::after": {
          borderBottom: `1px solid ${c.inputs.defaultFocus}`,
        },
        "&.Mui-error::before": {
          borderBottom: `1px solid ${c.inputs.defaultError}`,
          opacity: 1,
        },
        "&.Mui-error::after": {
          borderBottom: `1px solid ${c.inputs.defaultError}`,
          opacity: 1,
        },
      },
      input: {
        fontSize: "16px",
        lineHeight: "24px",
        "&::placeholder": {
          fontSize: "16px",
          lineHeight: "24px",
          color: c.text.primary,
          opacity: 1,
        },
        "&:focus::placeholder": {
          fontSize: "14px",
          lineHeight: "22px",
          fontWeight: fontWeights.semiBold,
        },
      },
    },
  },

  // -------------------------------------------------------------------------
  // FilledInput - filled variant input styling
  // -------------------------------------------------------------------------
  MuiFilledInput: {
    styleOverrides: {
      root: {
        boxSizing: "border-box",
        borderRadius: `${borderRadius.full}px`,
        outline: `1px solid ${c.inputs.defaultDefault}`,
        backgroundColor: "#FFF",

        "&:hover": {
          backgroundColor: "#FFF",
          outline: `1px solid ${c.inputs.defaultHover}`,
        },
        "&.Mui-focused": {
          backgroundColor: "#FFF",
          outline: `1.5px solid ${c.inputs.defaultFocus}`,
        },
        "&.Mui-error": {
          border: `1.5px solid ${c.inputs.defaultError}`,
          outline: "none",
          "&:hover": {
            outline: "none",
          },
        },
        "&.Mui-disabled": {
          backgroundColor: c.bg.disabled,
        },

        // Remove default underline
        "&::after": { borderBottom: "none !important" },
        "&::before": { borderBottom: "none !important" },
      },
    },
  },

  // -------------------------------------------------------------------------
  // OutlinedInput - outlined variant input styling
  // -------------------------------------------------------------------------
  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        borderRadius: `${borderRadius.full}px`,
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: c.inputs.defaultDefault,
        },
        "&:hover .MuiOutlinedInput-notchedOutline": {
          borderColor: c.inputs.defaultHover,
        },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
          borderColor: c.inputs.defaultFocus,
        },
        "&.Mui-error .MuiOutlinedInput-notchedOutline": {
          borderColor: c.inputs.defaultError,
        },
      },
    },
  },

  // -------------------------------------------------------------------------
  // InputLabel - floating label styling
  // -------------------------------------------------------------------------
  MuiInputLabel: {
    styleOverrides: {
      root: {
        lineHeight: "normal",
        // EGDE-210: the resting label acts as the field placeholder across every
        // form, so it must read black per the Figma design (was muted grey).
        //
        // EGDE-666: black is now the label's colour in EVERY state. QA measured
        // #6D6D6D on two of them and asked for black on both — the floated
        // label of a field that already holds text ("the text should be in
        // black — text field with inserted text") and the resting placeholder
        // of a field in error ("the placeholder text should appear in black").
        // Focus is folded in for the same reason: with the other two black, a
        // grey focused label made the same words change colour mid-interaction
        // (black → grey on click → black on blur). The error signal stays the
        // red outline plus the red helper text.
        //
        // The state selectors are spelled out rather than deleted: MUI's own
        // FormLabel styles carry `.Mui-error`/`.Mui-focused` rules of equal
        // specificity, so dropping ours just hands the label back to MUI's
        // palette (error red / primary orange) instead of leaving it black.
        color: c.text.primary,
        "&.MuiInputLabel-shrink": { color: c.text.primary },
        "&.Mui-focused": { color: c.text.primary },
        "&.Mui-error": { color: c.text.primary },
      },
    },
  },

  // -------------------------------------------------------------------------
  // FormHelperText - validation/helper text below inputs
  // -------------------------------------------------------------------------
  MuiFormHelperText: {
    styleOverrides: {
      root: {
        // EGDE-666: QA measured the validation message at 12px and specified
        // 14px. 18px leading is the body3 mobile pair for that size.
        fontSize: "14px",
        lineHeight: "18px",
        marginTop: "4px",
        "&.Mui-error": { color: c.border.error },
      },
    },
  },

  // -------------------------------------------------------------------------
  // Select - dropdown select styling
  // -------------------------------------------------------------------------
  MuiSelect: {
    styleOverrides: {
      root: {
        boxSizing: "border-box",
        borderRadius: `${borderRadius.full}px`,
        outline: `1px solid ${c.inputs.dropDownDefault}`,
        backgroundColor: "#FFF",

        "&:hover": {
          backgroundColor: "#FFF",
          outline: `1px solid ${c.inputs.dropDownHover}`,
        },
        "&.Mui-focused": {
          backgroundColor: "#FFF",
          outline: `1px solid ${c.inputs.dropDownFocus}`,
        },
        fieldset: {
          border: "none",
          outline: "none",
        },
        "&.Mui-error": {
          border: `1.5px solid ${c.inputs.dropDownError}`,
          outline: "none",
          "&:hover": { outline: "none" },
        },

        // Remove default underline
        "&::after": { borderBottom: "none !important" },
        "&::before": { borderBottom: "none !important" },
      },
    },
  },

  // -------------------------------------------------------------------------
  // Tabs - tab bar container
  // -------------------------------------------------------------------------
  MuiTabs: {
    styleOverrides: {
      flexContainer: {
        gap: "16px",
        borderBottom: `2px solid ${c.border.default}`,
        width: "fit-content",
      },
      indicator: {
        display: "none",
      },
    },
  },

  // -------------------------------------------------------------------------
  // Tab - individual tab styling
  // Uses gradient bottom border for selected state
  // -------------------------------------------------------------------------
  MuiTab: {
    styleOverrides: {
      root: {
        height: 36,
        padding: "0 0 8px 0",
        fontSize: "16px",
        lineHeight: "26px",
        fontWeight: fontWeights.medium,
        textTransform: "none",
        color: c.text.disabled,
        borderRadius: 0,
        marginBottom: "-2px",

        "&.Mui-selected": {
          color: c.text.primary,
          fontWeight: fontWeights.bold,
          borderImage: `linear-gradient(90deg, ${colors.primitives.primary500}, ${colors.primitives.secondary500}, ${colors.primitives.warning500}) 1`,
          borderBottom: "2px solid",
        },
        "&:hover": {
          color: c.text.primary,
        },
        [mediaQueries.mobile]: {
          height: 30,
          fontSize: "18px",
        },
      },
    },
  },

  // -------------------------------------------------------------------------
  // Checkbox
  // -------------------------------------------------------------------------
  MuiCheckbox: {
    defaultProps: {
      disableRipple: true,
    },
    styleOverrides: {
      root: {
        padding: "8px",
        color: colors.primitives.neutral300,
        "&.Mui-checked": { color: colors.primitives.primary500 },
        "&.Mui-disabled": {
          color: colors.primitives.neutral200,
        },
        "&:focus-visible": {
          outline: "2px solid black",
          outlineOffset: "2px",
        },
      },
    },
  },

  // -------------------------------------------------------------------------
  // Radio
  // -------------------------------------------------------------------------
  MuiRadio: {
    defaultProps: {
      disableRipple: true,
    },
    styleOverrides: {
      root: {
        padding: "8px",
        color: colors.primitives.neutral300,
        "&.Mui-checked": { color: colors.primitives.primary500 },
        "&.Mui-disabled": {
          color: colors.primitives.neutral300,
        },
        "&:focus-visible": {
          outline: "2px solid black",
          outlineOffset: "2px",
        },
      },
    },
  },

  // -------------------------------------------------------------------------
  // Dialog - modal dialogs
  // -------------------------------------------------------------------------
  MuiDialog: {
    styleOverrides: {
      paper: {
        borderRadius: `${borderRadius[16]}px`,
        boxShadow: colors.shadows.shadowHeader,
      },
    },
  },

  // -------------------------------------------------------------------------
  // Backdrop - overlay behind dialogs/drawers
  // -------------------------------------------------------------------------
  MuiBackdrop: {
    styleOverrides: {
      root: {
        backgroundColor: "rgba(0, 0, 0, 0.5)",
      },
    },
  },

  // -------------------------------------------------------------------------
  // Tooltip
  // -------------------------------------------------------------------------
  MuiTooltip: {
    styleOverrides: {
      tooltip: {
        borderRadius: `${borderRadius[8]}px`,
        fontSize: "14px",
        lineHeight: "20px",
        backgroundColor: c.bg.inverse,
        color: c.text.secondary,
      },
    },
  },

  // -------------------------------------------------------------------------
  // Chip
  // -------------------------------------------------------------------------
  MuiChip: {
    styleOverrides: {
      root: {
        borderRadius: `${borderRadius.full}px`,
        fontFamily: `${fontFamily.primary}, sans-serif`,
      },
    },
  },

  // -------------------------------------------------------------------------
  // Link
  // -------------------------------------------------------------------------
  MuiLink: {
    styleOverrides: {
      root: {
        color: c.text.brand,
        textDecoration: "none",
        "&:hover": {
          textDecoration: "underline",
        },
        "&:focus-visible": {
          outline: "2px solid black",
          outlineOffset: "2px",
        },
      },
    },
  },
};

// =============================================================================
// CREATE THEME
// Assembles all the pieces into a single MUI theme object.
// =============================================================================
export const theme = createTheme({
  direction: "rtl",
  palette,
  typography: typographyConfig,
  components,
  breakpoints: {
    values: {
      xs: breakpoints.xs,
      sm: breakpoints.sm,
      md: breakpoints.md,
      lg: breakpoints.lg,
      xl: breakpoints.xl,
    },
  },
  shape: {
    borderRadius: borderRadius[8],
  },
  // Custom theme properties (accessible via useTheme())
  colors,
  sizes,
  mediaQueries,
});

// =============================================================================
// RE-EXPORTS
// Consumers can import everything from theme.ts as a single entry point.
// =============================================================================
export { colors, sharedColors } from "./colors";
export { sizes, borderRadius, spacing, padding, iconSizes, avatarSizes } from "./sizes";
export { breakpoints, mediaQueries, mediaQueriesValues, layoutPadding } from "./mediaQueries";
export {
  fontFamily,
  fontWeights,
  typographyVariants,
  typography,
} from "./typography";
export { GlobalStyle } from "./GlobalStyle";

/** @deprecated Use `theme` instead */
export const sharedTheme = theme;

export type { Theme } from "@mui/material/styles";
